import React from 'react';
import css from './Table.module.scss';
import SortVertical01 from '../Icons/SortVertical01'
import ChevronUp from '../Icons/ChevronUp'
import ChevronDown from '../Icons/ChevronDown'
import PropTypes from 'prop-types';
import {useTableContext} from './TableContext';
import ColumnResizeHandle from './ColumnResizeHandle';

export const tableSortOrders = {
  ASC: 'asc',
  DESC: 'desc',
};

const ARIA_SORT = {
  [tableSortOrders.ASC]: 'ascending',
  [tableSortOrders.DESC]: 'descending',
};

const SortIcon = ({sortOrder}) => {
  if (sortOrder === tableSortOrders.ASC) {
    return <ChevronUp className={css.sortDirection} />;
  }
  if (sortOrder === tableSortOrders.DESC) {
    return <ChevronDown className={css.sortDirection} />;
  }

  return <SortVertical01 />;
};

const TableSortableHeader = ({
  width,
  children,
  style,
  sortable,
  onSortColumn,
  index,
  align,
  className = '',
  rowSpan,
  colSpan,
  resizable = false,
  columnId,
  sortOrder,
}) => {
  const {cellAlign, columnWidths} = useTableContext();
  const finalAlign = align ?? cellAlign ?? 'left';
  const isLeaf = !colSpan || Number(colSpan) <= 1;
  const isResizable = resizable && isLeaf && columnId !== undefined && columnId !== null;
  const storedWidth = columnId !== undefined && columnId !== null ? columnWidths[columnId] : undefined;
  const appliedWidth = storedWidth ?? width;
  const isSorted = sortable && Boolean(ARIA_SORT[sortOrder]);

  const handleClick = () => {
    if (sortable) {
      onSortColumn(index);
    }
  };

  const content = sortable
    ? (
      <span className={css.sortableLabel}>
        <span>{children}</span>
        <SortIcon sortOrder={sortOrder} />
      </span>
    )
    : children;

  return (
    <th
      data-column-id={columnId ?? undefined}
      className={`${css.th} ${sortable ? css.thSortable : ''} ${isSorted ? css.thSorted : ''} ${isResizable ? css.thResizable : ''} ${className}`.trim()}
      aria-sort={isSorted ? ARIA_SORT[sortOrder] : undefined}
      style={{width: appliedWidth ?? undefined, cursor: sortable ? 'pointer' : 'default', textAlign: finalAlign, ...style}}
      onClick={sortable ? handleClick : undefined}
      rowSpan={rowSpan}
      colSpan={colSpan}
    >
      {content}
      {isResizable &&
        <ColumnResizeHandle columnId={columnId} />
      }
    </th>
  );
};

TableSortableHeader.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
  style: PropTypes.object,
  className: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  rowSpan: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  colSpan: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sortable: PropTypes.bool,
  onSortColumn: PropTypes.func,
  index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resizable: PropTypes.bool,
  columnId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sortOrder: PropTypes.oneOf([tableSortOrders.ASC, tableSortOrders.DESC]),
};

export default TableSortableHeader;
